import {
    SearchFilterDefinition,
    SearchFilterImpact,
    SearchFilterOption,
    SearchFilterSelection,
    SearchFilterSelectionValue,
    SearchFilterValue,
    SearchFilterViewModel,
    isSearchFilterSelectionArray,
} from './filter-state.models';

export class FilterStateEngine<TValue extends SearchFilterValue = SearchFilterValue> {
    constructor(private readonly definitions: readonly SearchFilterDefinition<TValue>[]) {}

    public getViewModels(selection: SearchFilterSelection<TValue>): SearchFilterViewModel<TValue>[] {
        const normalizedSelection = this.normalizeSelection(selection);
        return this.definitions.map((definition, index) => this.getViewModel(definition, index, normalizedSelection));
    }

    public select(
        selection: SearchFilterSelection<TValue>,
        key: string,
        value: SearchFilterSelectionValue<TValue>,
    ): SearchFilterSelection<TValue> {
        const keyIndex = this.indexOf(key);
        if (keyIndex === -1) {
            return this.normalizeSelection(selection);
        }

        const nextSelection: SearchFilterSelection<TValue> = {
            ...this.normalizeSelection(selection),
            [key]: value,
        };

        for (const [index, definition] of this.definitions.entries()) {
            if (index <= keyIndex) {
                continue;
            }

            const impact = this.getImpact(definition, index, nextSelection);
            const currentValue = nextSelection[definition.key];
            const optionIsAllowed = currentValue === null || currentValue === undefined ||
                this.isAllowedValue(currentValue, impact, definition.options);

            if (impact?.resetSelection || impact?.enabled === false || !optionIsAllowed) {
                delete nextSelection[definition.key];
            }
        }

        if (value === null || value === undefined || (isSearchFilterSelectionArray(value) && value.length === 0)) {
            delete nextSelection[key];
        }

        return nextSelection;
    }

    private getViewModel(
        definition: SearchFilterDefinition<TValue>,
        index: number,
        selection: SearchFilterSelection<TValue>,
    ): SearchFilterViewModel<TValue> {
        const impact = this.getImpact(definition, index, selection);
        const options = impact?.allowedOptionValues
            ? definition.options.filter(option => impact.allowedOptionValues.some(value => this.sameValue(value, option.value)))
            : definition.options;

        const selectedValue = selection[definition.key];
        const normalizedSelectedValue = this.normalizeSelectedValue(definition, selectedValue, options);
        return {
            definition,
            options,
            selectedValue: normalizedSelectedValue,
            enabled: definition.enabled !== false && impact?.enabled !== false,
        };
    }

    private normalizeSelectedValue(
        definition: SearchFilterDefinition<TValue>,
        value: SearchFilterSelectionValue<TValue>,
        options: readonly SearchFilterOption<TValue>[],
    ): SearchFilterSelectionValue<TValue> {
        if (value === undefined || value === null) {
            return definition.multiple ? [] : null;
        }

        if (definition.multiple) {
            const values = this.toScalarValues(value);
            return values.filter(selectedValue => options.some(option => this.sameValue(option.value, selectedValue)));
        }

        if (isSearchFilterSelectionArray(value)) {
            const values = this.toScalarValues(value);
            return values.length > 0
                ? options.find(option => this.sameValue(option.value, values[0]))?.value ?? null
                : null;
        }

        return options.find(option => this.sameValue(option.value, value as TValue))?.value ?? null;
    }

    private getImpact(
        definition: SearchFilterDefinition<TValue>,
        index: number,
        selection: SearchFilterSelection<TValue>,
    ): SearchFilterImpact<TValue> {
        if (!definition.impact) {
            return {};
        }

        return definition.impact({
            selection,
            currentFilter: definition,
            previousFilter: this.definitions[index - 1],
        }) ?? {};
    }

    private isAllowedValue(
        value: SearchFilterSelectionValue<TValue>,
        impact: SearchFilterImpact<TValue>,
        options: readonly SearchFilterOption<TValue>[],
    ): boolean {
        if (value === null || value === undefined) {
            return true;
        }

        const values = this.toScalarValues(value);
        if (impact.allowedOptionValues) {
            return values.every(selectedValue => impact.allowedOptionValues.some(optionValue => this.sameValue(optionValue, selectedValue)));
        }

        return values.every(selectedValue => options.some(option => this.sameValue(option.value, selectedValue)));
    }

    private sameValue(left: TValue, right: TValue): boolean {
        return String(left) === String(right);
    }

    private toScalarValues(value: SearchFilterSelectionValue<TValue>): TValue[] {
        return isSearchFilterSelectionArray(value) ? [...value] as TValue[] : [value as TValue];
    }

    private indexOf(key: string): number {
        return this.definitions.findIndex(definition => definition.key === key);
    }

    private normalizeSelection(selection: SearchFilterSelection<TValue>): SearchFilterSelection<TValue> {
        const configuredKeys = new Set(this.definitions.map(definition => definition.key));
        return Object.fromEntries(
            Object.entries(selection)
                .filter(([key]) => configuredKeys.has(key))
                .map(([key, value]) => {
                    const definition = this.definitions.find(item => item.key === key);
                    return [
                        key,
                        definition?.multiple && value !== null && value !== undefined && !isSearchFilterSelectionArray(value)
                            ? [value as TValue]
                            : value,
                    ];
                })
        );
    }
}
